/**
 * CDM Compliance — CIS Critical Security Controls v8 scorecard.
 *
 * Per docs/cdm-design-v4.md §13.4: framework + control_id + per-asset
 * roll-up + org-level findings. Auditors recognise the format.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Scale, RefreshCw, ChevronLeft, CheckCircle2, XCircle,
  AlertTriangle, MinusCircle,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface ControlSummary {
  pass: number
  fail: number
  not_applicable: number
  pass_pct: number | null
  description: string
  severity: string
}

interface OrgFinding {
  rule_id: string
  framework: string
  control_id: string
  status: string
  severity: string
  description: string
  evidence: any
}

interface Scorecard {
  framework: string
  rules: number
  asset_count: number
  org_findings: OrgFinding[]
  org_pass: number
  org_fail: number
  asset_summary: Record<string, ControlSummary>
  overall_compliance_pct: number | null
}

const SEVERITY_TONE: Record<string, string> = {
  critical: 'text-rose-light',
  high:     'text-amber-light',
  medium:   'text-cyan',
  low:      'text-text-muted',
}

const STATUS_ICON: Record<string, React.ElementType> = {
  pass:           CheckCircle2,
  fail:           XCircle,
  not_applicable: MinusCircle,
}

const STATUS_TONE: Record<string, string> = {
  pass:           'text-emerald',
  fail:           'text-rose-light',
  not_applicable: 'text-text-muted',
}

const FRAMEWORKS = [
  { id: 'CIS-v8',         label: 'CIS Controls v8' },
  { id: 'NIST-CSF-1.1',   label: 'NIST CSF 1.1' },
]

export default function CdmCompliancePage() {
  const [data, setData] = useState<Scorecard | null>(null)
  const [loading, setLoading] = useState(false)
  const [framework, setFramework] = useState('CIS-v8')

  const load = () => {
    setLoading(true)
    api.get('/api/cdm/compliance/scorecard', { params: { framework } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [framework])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Scale size={18} className="text-purple-light" /> CDM COMPLIANCE
        </h1>
        <select className="input text-[11px]" value={framework}
                onChange={e => setFramework(e.target.value)}
                aria-label="Compliance framework">
          {FRAMEWORKS.map(f => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <button type="button" onClick={load} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && <div className="card p-6 text-text-muted text-sm">Evaluating rules…</div>}

      {data && (
        <>
          {/* Overall compliance hero */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="card p-4 col-span-1 md:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-text-muted">Overall compliance</div>
              <div className={clsx(
                'text-4xl font-black font-mono mt-1',
                (data.overall_compliance_pct ?? 0) >= 0.95 ? 'text-emerald' :
                (data.overall_compliance_pct ?? 0) >= 0.80 ? 'text-amber-light' :
                                                              'text-rose-light',
              )}>
                {data.overall_compliance_pct !== null
                  ? `${(data.overall_compliance_pct * 100).toFixed(1)}%`
                  : 'no data'}
              </div>
              <div className="text-[10px] text-text-muted mt-1">
                {data.framework} · {data.rules} rules · {data.asset_count} assets
              </div>
            </div>
            <div className="card p-3">
              <div className="text-[10px] uppercase tracking-widest text-text-muted">Org checks</div>
              <div className="text-xl font-black font-mono mt-1">
                <span className="text-emerald">{data.org_pass}</span>
                <span className="text-text-muted mx-1">/</span>
                <span className="text-rose-light">{data.org_fail}</span>
              </div>
              <div className="text-[10px] text-text-muted">pass / fail</div>
            </div>
            <div className="card p-3">
              <div className="text-[10px] uppercase tracking-widest text-text-muted">Controls evaluated</div>
              <div className="text-xl font-black font-mono mt-1 text-blue-light">{Object.keys(data.asset_summary).length + data.org_findings.length}</div>
            </div>
          </div>

          {/* Per-control scorecard */}
          <div className="card p-4">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Per-control scorecard (asset-scope rules)</h2>
            <div className="space-y-2">
              {Object.entries(data.asset_summary).map(([control_id, s]) => {
                const tested = s.pass + s.fail
                const pct = tested ? (s.pass / tested) : null
                return (
                  <div key={control_id} className="flex items-center gap-3 p-2 rounded border border-border bg-surface-card">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-mono font-bold text-text-primary">{control_id}</code>
                        <span className={clsx('text-[10px]', SEVERITY_TONE[s.severity])}>{s.severity}</span>
                      </div>
                      <div className="text-[11px] text-text-secondary truncate">{s.description}</div>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] font-mono">
                      <span className="text-emerald">{s.pass} pass</span>
                      <span className="text-rose-light">{s.fail} fail</span>
                      {s.not_applicable > 0 && <span className="text-text-muted">{s.not_applicable} n/a</span>}
                    </div>
                    <div className="w-24">
                      {pct !== null && (
                        <div className="bg-surface-raised h-1.5 rounded overflow-hidden">
                          <div
                            className={clsx(
                              'h-full transition-all',
                              pct >= 0.95 ? 'bg-emerald' : pct >= 0.80 ? 'bg-amber' : 'bg-rose-light'
                            )}
                            style={{ width: `${pct * 100}%` }}
                          />
                        </div>
                      )}
                    </div>
                    <div className={clsx(
                      'w-12 text-right font-mono text-xs font-bold',
                      pct === null ? 'text-text-muted' :
                      pct >= 0.95 ? 'text-emerald' :
                      pct >= 0.80 ? 'text-amber-light' : 'text-rose-light',
                    )}>
                      {pct !== null ? `${(pct * 100).toFixed(0)}%` : '—'}
                    </div>
                  </div>
                )
              })}
              {Object.keys(data.asset_summary).length === 0 && (
                <p className="text-xs text-text-muted">No asset-scope controls evaluated (no assets in CDM yet).</p>
              )}
            </div>
          </div>

          {/* Org-scope findings */}
          <div className="card p-4">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Org-scope findings</h2>
            <div className="space-y-2">
              {data.org_findings.map(f => {
                const Icon = STATUS_ICON[f.status] || AlertTriangle
                return (
                  <div key={f.control_id} className={clsx(
                    'flex items-start gap-2 p-2 rounded border',
                    f.status === 'pass'
                      ? 'border-emerald/30 bg-emerald/5'
                      : f.status === 'fail'
                        ? 'border-rose/30 bg-rose/5'
                        : 'border-border bg-surface-card',
                  )}>
                    <Icon size={14} className={clsx('mt-0.5 flex-shrink-0', STATUS_TONE[f.status])} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <code className="text-xs font-mono font-bold text-text-primary">{f.control_id}</code>
                        <span className={clsx('text-[10px]', SEVERITY_TONE[f.severity])}>{f.severity}</span>
                      </div>
                      <div className="text-[11px] text-text-secondary">{f.description}</div>
                      <pre className="text-[10px] text-text-muted font-mono mt-1 overflow-x-auto">{JSON.stringify(f.evidence, null, 0)}</pre>
                    </div>
                  </div>
                )
              })}
              {data.org_findings.length === 0 && (
                <p className="text-xs text-text-muted">No org-scope findings yet.</p>
              )}
            </div>
          </div>

          <p className="text-[10px] text-text-muted text-center">
            Phase 10.5 adds DORA-ICT framework. NIST CSF + ISO 27001 follow as the rule pack expands.
          </p>
        </>
      )}
    </div>
  )
}
